import { getPbisPointsWireEventNames } from "@/constants/behaviourPbisSocketConstants.js"

/** @type {Set<(studentIds: number[]) => void>} */
const refreshHandlers = new Set()

/**
 * @param {(studentIds: number[]) => void} handler
 * @returns {() => void}
 */
export function registerPbisPointsRefreshHandler(handler) {
  if (typeof handler !== "function") {
    return () => {}
  }

  refreshHandlers.add(handler)
  return () => refreshHandlers.delete(handler)
}

/**
 * @param {unknown} channelName
 * @returns {string}
 */
export function normalizePbisPointsChannelName(channelName) {
  if (typeof channelName !== "string" || !channelName) {
    return ""
  }

  return channelName.startsWith("private-")
    ? channelName
    : `private-${channelName}`
}

/**
 * @param {unknown} payload
 * @returns {Record<string, unknown>}
 */
export function normalizePbisPointsMutationPayload(payload) {
  if (typeof payload === "string") {
    try {
      const parsed = JSON.parse(payload)
      return typeof parsed === "object" && parsed != null ? parsed : {}
    } catch {
      return {}
    }
  }

  if (payload && typeof payload === "object") {
    const data = payload?.data
    if (
      data &&
      typeof data === "object" &&
      (data?.student_ids != null ||
        data?.studentIds != null ||
        data?.mutation_type != null)
    ) {
      return data
    }
    return payload
  }

  return {}
}

/**
 * @param {unknown} payload
 * @returns {boolean}
 */
export function isPbisPointsMutationPayload(payload) {
  const normalized = normalizePbisPointsMutationPayload(payload)
  return Boolean(normalized?.mutation_type)
}

/**
 * @param {unknown} payload
 * @returns {number[]}
 */
export function normalizePbisPointsMutationStudentIds(payload) {
  const normalized = normalizePbisPointsMutationPayload(payload)
  const raw = normalized?.student_ids ?? normalized?.studentIds ?? []
  if (!Array.isArray(raw)) {
    return []
  }
  return raw.map((id) => Number(id)).filter((id) => Number.isFinite(id))
}

/**
 * @param {import('vuex').Store} store
 * @param {unknown} payload
 */
export function dispatchPbisPointsRefresh(store, payload) {
  if (!isPbisPointsMutationPayload(payload)) {
    return
  }

  const studentIds = normalizePbisPointsMutationStudentIds(payload)

  store.commit("behaviourManage/notifyPbisPointsMutated", { studentIds })

  refreshHandlers.forEach((handler) => {
    try {
      handler(studentIds)
    } catch {
      // Ignore handler failures so one view cannot break others.
    }
  })
}

/**
 * @param {import('vuex').Store} store
 * @param {unknown} payload
 */
export function notifyPbisPointsMutated(store, payload) {
  dispatchPbisPointsRefresh(store, payload)
}

/**
 * @param {import('laravel-echo').default['private']} channel
 * @param {import('vuex').Store} store
 * @param {{ onMutation?: (payload: Record<string, unknown>) => void }} [options]
 */
export function attachPbisPointsSocketListeners(channel, store, options = {}) {
  if (!channel?.listen || !store) {
    return
  }

  const onMutation = (payload) => {
    dispatchPbisPointsRefresh(store, payload)
    options.onMutation?.(payload)
  }

  getPbisPointsWireEventNames().forEach((eventName) => {
    channel.listen(`.${eventName}`, onMutation)
  })
}
